import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlined";
import { Button } from "@/components/common/Button";
import { Callout } from "@/components/common/Callout";
import { Card } from "@/components/common/Card";
import { FigureTable, type FigureRow } from "components/common/FigureTable";
import { Spinner } from "@/components/common/Spinner";
import { StatusChip } from "@/components/common/StatusChip";
import { plural } from "@/components/common/setUtils";
import { useAppAction, useRun } from "@/lib/api/apps/hooks";
import { appKeys } from "@/lib/api/apps/keys";
import { OpeningSpinner } from "@/components/main/overview/OpeningSpinner";
import { Tooltip } from "@/components/common/Tooltip";
import type {
  AppDetail,
  RoundCost,
  RunSummary,
  RunUsage,
} from "@/lib/api/apps/types";
import {
  endReasonWords,
  usd,
  usdMicros,
  webCalls,
  whenLabel,
} from "../app-format";
import { UsageCard } from "../design/usage-card";
import { formatTime } from "@/lib/dates";
import { useNow } from "../design/draft-model";
import { RefusalCallout } from "@/components/authentication/consent/RefusalCallout";

/** A round of questions or a run, as the page names it. */
export function stepName(run: RunSummary): string {
  return run.kind === "ask"
    ? `Clarifications · round ${run.ask_round ?? 1}`
    : `Run ${run.number}`;
}

/** "45 s", "12 min 4 s". */
export function durationLabel(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
}

/** A model's share of a run as the design app's cost card reads it. */
export function costOf(u: RunUsage): RoundCost {
  const usage = {
    input: u.input_tokens,
    cache_write: u.cache_write_tokens,
    cache_read: u.cache_read_tokens,
    output: u.output_tokens,
  };
  return {
    provider: u.provider,
    model: u.model,
    tokens: usage.input + usage.cache_write + usage.cache_read + usage.output,
    micros: u.cost_micros,
    usage: { ...usage, price: u.price },
  };
}

/** A run's spend from the ledger: each model, its cost split by kind of
 *  token on hover, then the web lookups, then the total. */
export function usageRows(run: RunSummary): FigureRow[] {
  const rows: FigureRow[] = [];
  let searches = 0;
  let reads = 0;
  let webMicros = 0;
  for (const u of run.usage) {
    rows.push({
      key: u.model,
      cells: [
        u.model_name,
        plural(u.calls, "call"),
        <Tooltip
          key="cost"
          title={<UsageCard costs={[costOf(u)]} title={u.model_name} />}
        >
          <span>{usdMicros(u.cost_micros)}</span>
        </Tooltip>,
      ],
    });
    searches += u.searches;
    reads += u.page_reads;
    webMicros += u.web_cost_micros;
  }
  if (searches > 0 || reads > 0)
    rows.push({
      key: "search",
      cells: ["Web lookups", webCalls(searches, reads), usdMicros(webMicros)],
    });
  const total = run.usage.length
    ? run.usage.reduce((n, u) => n + u.cost_micros + u.web_cost_micros, 0)
    : run.spent_micros;
  rows.push({
    key: "total",
    tone: "total",
    cells: ["Total", "", usdMicros(total)],
  });
  return rows;
}

/** Every run and round, newest first, with what each did and spent; the live
 *  one on top with its activity ticking. */
export function RunsTab({
  app,
  onAnswer,
}: {
  app: AppDetail;
  /** To the Clarifications tab, while the agent waits on answers. */
  onAnswer: () => void;
}) {
  const live = app.current_run;
  const now = useNow(Boolean(live));
  return (
    <div className="flex flex-col gap-4">
      <RunControls app={app} onAnswer={onAnswer} />
      {app.runs.length === 0 && (
        <p className="m-0 text-fg-muted">
          {app.status === "active"
            ? "Run 1 starts in a moment."
            : "No runs yet."}
        </p>
      )}
      {app.runs.map((run) => (
        <RunCard key={run.run_id} appId={app.app_id} run={run} now={now} />
      ))}
    </div>
  );
}

/** How often the page asks whether a run it started has begun. */
const STARTING_POLL_MS = 1_000;
/** How long it waits for one before it shows what the app says instead. */
const STARTING_LIMIT_MS = 60_000;

/** Between a press of Run and the run's row, which the agent writes once it
 *  has its provider, claim and token: "Starting agent", polled each second,
 *  so the new run's card takes the line's place with no gap and no second
 *  press. `firstRunId` is the newest run when it was pressed. */
function useStarting(app: AppDetail) {
  const client = useQueryClient();
  const [starting, setStarting] = useState<{
    firstRunId: string | null;
  } | null>(null);
  // A run that pauses before it writes its row (no key, no owner) has nothing
  // to wait for once the app, made active by the press, is paused again.
  const wasActive = useRef(false);
  if (starting !== null && app.status === "active") wasActive.current = true;
  const begun =
    starting !== null &&
    (Boolean(app.current_run) ||
      (app.runs[0]?.run_id ?? null) !== starting.firstRunId ||
      (wasActive.current && app.status === "paused"));
  useEffect(() => {
    if (begun) setStarting(null);
  }, [begun]);
  useEffect(() => {
    if (!starting) return;
    const poll = setInterval(
      () =>
        void client.invalidateQueries({ queryKey: appKeys.detail(app.app_id) }),
      STARTING_POLL_MS,
    );
    const limit = setTimeout(() => setStarting(null), STARTING_LIMIT_MS);
    return () => {
      clearInterval(poll);
      clearTimeout(limit);
    };
  }, [starting, client, app.app_id]);
  return {
    starting: starting !== null && !begun,
    start: () => {
      wasActive.current = false;
      setStarting({ firstRunId: app.runs[0]?.run_id ?? null });
    },
    stop: () => setStarting(null),
  };
}

/** What happens next and the one control that changes it. */
function RunControls({
  app,
  onAnswer,
}: {
  app: AppDetail;
  onAnswer: () => void;
}) {
  const pause = useAppAction(app.app_id, "pause");
  const resume = useAppAction(app.app_id, "resume");
  const { starting, start, stop } = useStarting(app);
  const busy = pause.isPending || resume.isPending;
  const error = pause.error ?? resume.error;
  const running = app.current_run?.kind === "run";
  const run = (label: string) => (
    <Button
      size="sm"
      variant="secondary"
      icon={<PlayCircleOutlineIcon className="icon-sm" />}
      disabled={busy}
      onClick={() => {
        start();
        resume.mutate(undefined, { onError: stop });
      }}
    >
      {label}
    </Button>
  );
  // The header carries the status; this line says what happens next.
  let line = app.next_run_at
    ? `Next run ${whenLabel(app.next_run_at)}`
    : app.last_run_at
      ? `Last run ${whenLabel(app.last_run_at)}`
      : "";
  let action: React.ReactNode = null;
  if (starting) {
    line = "";
  } else if (app.status === "paused") {
    line = "Nothing runs until you resume";
    action = run("Resume");
  } else if (app.status === "finished") action = run("Run again");
  else if (app.step === "ask") {
    line = "The agent asks before the next run";
    action = (
      <Button size="sm" variant="primary" onClick={onAnswer}>
        Answer
      </Button>
    );
  } else if (running) {
    line = `${stepName(app.current_run!)} is running`;
    action = (
      <Button
        size="sm"
        variant="secondary"
        icon={<PauseCircleOutlineIcon className="icon-sm" />}
        loading={pause.isPending}
        disabled={busy}
        onClick={() => pause.mutate()}
      >
        Stop and pause
      </Button>
    );
  } else if (app.status === "active") action = run("Run now");
  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex flex-wrap items-center gap-3"
        style={{ minHeight: "var(--item-height)" }}
      >
        {starting ? (
          <span className="flex flex-1 items-center gap-2 text-fg">
            <OpeningSpinner
              color="var(--color-fg-muted)"
              ariaLabel="Starting agent"
            />
            Starting agent
          </span>
        ) : (
          <span className="flex-1 text-fg">{line}</span>
        )}
        {action}
      </div>
      <RefusalCallout error={error} />
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-fg-muted-regular-small">{label}</span>
      <span className="font-mono text-fg">{value}</span>
    </div>
  );
}

/** The agent's note, four lines until opened: a run's can run to pages. */
function Note({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-fg-muted-regular-small">{label}</span>
      <p className={`m-0 text-fg ${open ? "" : "line-clamp-4"}`}>{text}</p>
      {text.length > 400 && (
        <Button
          variant="quiet-link"
          size="sm"
          className="self-start"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Show less" : "Show all"}
        </Button>
      )}
    </div>
  );
}

function RunCard({
  appId,
  run,
  now,
}: {
  appId: string;
  run: RunSummary;
  now: number;
}) {
  const live = run.state === "running";
  const [activity, setActivity] = useState(false);
  const events =
    useRun(appId, live || activity ? run.run_id : null, live).data?.events ??
    [];
  const latest = events[events.length - 1]?.summary;
  const round = run.kind === "ask";
  const ended = run.ended_at ? Date.parse(run.ended_at) : now;
  const figures: [string, string][] = [
    ["Time", durationLabel(ended - Date.parse(run.started_at))],
    ...(round
      ? []
      : ([
          ["Rows added", run.rows_added.toLocaleString()],
          ["Rows edited", run.rows_edited.toLocaleString()],
          ["Rows removed", run.rows_removed.toLocaleString()],
        ] as [string, string][])),
    ["Questions", String(run.questions)],
  ];
  const failed = run.state === "failed";
  return (
    <Card variant={live ? "accent" : "default"} padded={false}>
      <div
        className="flex flex-col gap-4"
        style={{ padding: "var(--spacing-lg)" }}
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-fg-semibold">{stepName(run)}</span>
          <StatusChip tone={live ? "accent" : failed ? "warning" : "muted"}>
            {run.state.replace("_", " ")}
          </StatusChip>
          <span className="text-fg-muted-regular-small">
            {whenLabel(run.started_at)}
          </span>
          <span className="flex-1" />
          <span className="font-mono">
            {usdMicros(run.spent_micros)}
            <span className="text-fg-muted"> of {usd(run.run_cap_cents)}</span>
          </span>
        </div>

        {live && (
          <div className="flex items-center gap-3" aria-live="polite">
            <Spinner size="sm" />
            <span className="truncate text-fg-muted">
              {latest ?? "Starting"}
            </span>
          </div>
        )}

        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(7rem, 1fr))" }}
        >
          {figures.map(([label, value]) => (
            <Figure key={label} label={label} value={value} />
          ))}
        </div>

        {failed && run.end_reason && (
          <Callout variant="error">{endReasonWords(run.end_reason)}</Callout>
        )}
        {!failed && !live && run.state !== "completed" && run.end_reason && (
          <span className="text-fg-muted">
            Ended: {endReasonWords(run.end_reason)}
          </span>
        )}
        {run.note && (
          <Note
            label={round ? "What it found" : "Note for the next run"}
            text={run.note}
          />
        )}

        <FigureTable
          head={["Spent on", "Amount", "Cost"]}
          rows={usageRows(run)}
        />

        <div className="flex flex-col gap-2">
          <Button
            variant="quiet-link"
            size="sm"
            className="self-start"
            aria-expanded={activity}
            onClick={() => setActivity((a) => !a)}
          >
            {activity
              ? "Hide activity"
              : `Activity · ${plural(run.tool_calls, "tool call")}`}
          </Button>
          {activity && (
            <ul className="list-none m-0 p-0 flex flex-col gap-1">
              {events.map((e) => (
                <li
                  key={e.event_id}
                  className="flex items-center gap-2 flex-wrap"
                >
                  <span className="text-mono-small-muted">
                    {formatTime(e.at)}
                  </span>
                  <StatusChip tone={e.kind === "ask" ? "accent" : "muted"}>
                    {e.tool ?? e.kind}
                  </StatusChip>
                  <span className="text-fg text-small">{e.summary}</span>
                  {e.cost_micros > 0 && (
                    <span className="ml-auto text-mono-small-muted">
                      {usdMicros(e.cost_micros)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
